import { useState, useRef, useEffect } from "react"
import { FileText, Copy, Check, BookOpen } from "lucide-react"
import { trackCitationClick } from "@/lib/citation-tracking"
import { MessageFeedback } from "@/components/assistant/MessageFeedback"
import { AttachmentChips } from "@/components/assistant/AttachmentChips"
import { ArtifactChip } from "@/components/assistant/ArtifactChip"
import { MemorySavedLines } from "@/components/chat/MemorySavedLines"
import ReactMarkdown from "react-markdown"
import { cn, markdownToPlaintext, markdownToHtml } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import type { ChatMessage } from "@/stores/assistant-store"
import { usePreviewStore } from "@/stores/preview-store"
import { useCitationMarkdown } from "@/hooks/useCitationMarkdown"
import { copyToClipboard } from "@/lib/clipboard"
import { TODO_REGEX, ATT_REGEX, LEGACY_ATTRIBUTION_REGEX } from "@/lib/annotations"
import { resolveCitationTargetPage, toPrecisePreviewHighlight } from "@/lib/citation-preview"
import { groupCitationsByDocument } from "@/lib/group-citation-chips"

interface MessageBubbleProps {
  message: ChatMessage
  /** Undo a "Saved to memory" line on this reply. Each door passes its own store's action. */
  onUndoMemory?: (messageId: string, memoryId: string) => void
}

/**
 * Remove authoring annotations from MARKDOWN bound for the clipboard.
 *
 * Deliberately NOT `stripAnnotations` from `@/lib/annotations`. That helper
 * finishes with a whole-string `[ \t]{2,}` → `' '` tidy, which is harmless on
 * the HTML it was written for but destructive on markdown, where leading
 * indentation is structural: `"    - Site setup"` collapses to a single space
 * and the item pastes into Word as a SIBLING of its parent, silently flattening
 * the nesting. `stripAnnotations` is a byte-identical mirror of the backend
 * scanner and must not be changed, so the copy path carries its own tidy.
 *
 * Same three token patterns and the same byte-identity guard — a message with
 * no tokens is returned untouched, so clean copies are provably unaffected.
 * The tidy is line-interior only: both passes require a non-whitespace
 * character before the run they collapse, so indentation at the start of a line
 * can never be a match.
 */
function stripAnnotationsForCopy(markdown: string): string {
  if (!markdown) return markdown

  // No `lastIndex` reset needed: String#replace with a /g regex rewinds the
  // cursor itself before scanning. (matchAll does not — see @/lib/annotations.)
  const out = markdown
    .replace(TODO_REGEX, '')
    .replace(ATT_REGEX, '')
    .replace(LEGACY_ATTRIBUTION_REGEX, '')

  if (out === markdown) return markdown

  return out
    // "a  b" → "a b", but "\n    - b" keeps its indent
    .replace(/(\S)[ \t]{2,}/g, '$1 ')
    // "Gate 3 ." → "Gate 3.", but an indented code line ("    .foo") is spared
    .replace(/(\S) ([.,;:!?])/g, '$1$2')
}

export function MessageBubble({ message, onUndoMemory }: MessageBubbleProps) {
  const isUser = message.role === "user"
  const isAssistant = message.role === "assistant"
  const sources = message.sources
  const librarySources = message.librarySources

  // Re-narrowed rather than cast at the call site: the chip takes the whole
  // message (it counts the draft's cited sources from the message's lists), and a
  // conditional inside JSX does not narrow the optional field for a prop.
  const artifactMessage = message.artifact ? { ...message, artifact: message.artifact } : null

  // Copy button state and ref
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  // Plugins, first-use renumbering and the citation chip mapping — shared with
  // the quick-write document panel so the two can never drift.
  const {
    normalizedContent,
    remarkPlugins,
    components,
    citationMapping,
    libraryCitationMapping,
    sourcesKey,
  } = useCitationMarkdown({
    content: message.content,
    sources,
    librarySources,
    isPending: message.isStreaming === true,
  })

  // Copy handler with timeout cleanup
  const handleCopy = async () => {
    // Copy runs over the RAW markdown, not the rendered DOM, so the chips the
    // reader sees do not protect this path: without the strip, a reply pasted
    // into Word carries `[ATT: …]` and `[TODO: …]` through verbatim (spec 19).
    // Both flavours are stripped — the HTML one is what Word actually takes.
    const copyable = stripAnnotationsForCopy(message.content)
    const plainText = markdownToPlaintext(copyable)
    const html = markdownToHtml(copyable)
    const success = await copyToClipboard(plainText, html)
    if (success) {
      // Clear existing timeout to avoid stacking on rapid clicks
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setCopied(true)
      timeoutRef.current = setTimeout(() => {
        setCopied(false)
        timeoutRef.current = null
      }, 2000)
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // No avatar on either side. The tinted right-aligned bubble already says
  // "you"; the assistant owns the whole surface, so a mark per message just
  // repeats itself. That left the row a single child, so the flex direction
  // toggle and its gap went with it.
  return (
    <div className="flex">
      {/* Content */}
      <div className={cn("flex-1 flex flex-col gap-1 min-w-0", isUser && "items-end")}>
        {/* Message bubble */}
        <div
          className={cn(
            "group",
            isUser && "rounded-lg px-3 py-1.5 bg-muted max-w-[85%]"
          )}
        >
          {isUser ? (
            <div>
              {/* Passage the user had highlighted in the document panel — shown
                  so the request reads as an edit to that passage, not a new ask */}
              {message.quotedText && (
                <div className="mb-1 border-l-2 border-border pl-2 text-meta italic truncate">
                  &ldquo;
                  {message.quotedText.length > 80
                    ? message.quotedText.slice(0, 80) + '…'
                    : message.quotedText}
                  &rdquo;
                </div>
              )}
              {/* Attached file tags */}
              {message.attachments && (
                <AttachmentChips attachments={message.attachments} />
              )}
              <p className="text-sm whitespace-pre-wrap leading-normal">
                {message.content}
              </p>
            </div>
          ) : (
            <>
              <div className="chat-markdown prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown
                  key={`md-${message.id}-${sourcesKey}`}
                  remarkPlugins={remarkPlugins}
                  components={components}
                >
                  {normalizedContent}
                </ReactMarkdown>
              </div>
              {/* Quick-write document produced by this turn */}
              {artifactMessage && <ArtifactChip message={artifactMessage} />}
            </>
          )}

          {/* Action buttons - only for assistant, non-streaming messages */}
          {isAssistant && !message.isStreaming && (
            <div className="flex items-center gap-0.5 mt-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation()
                  void handleCopy()
                }}
                aria-label="Copy message"
                title="Copy message"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </Button>
              <MessageFeedback messageId={message.id} sessionId={message.sessionId ?? ''} />
            </div>
          )}
        </div>

        {/* Partial message indicator */}
        {message.isPartial && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
              <span className="text-xs font-medium">
                Connection interrupted - attempting recovery...
              </span>
            </div>
          </div>
        )}

        {/* Sources summary (below message) - sequential numbering with citation prefix.
            The used-citation guard matches the library row below: without it the
            "Tender:" label can render on its own when nothing was actually cited. */}
        {sources && sources.length > 0 && !message.isStreaming && citationMapping.usedCitations.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="text-[10px] font-medium text-muted-foreground self-center">Tender:</span>
            {groupCitationsByDocument(
              citationMapping.usedCitations,
              citationMapping.origToDisplay,
              sources,
            ).map((group) => {
              const firstChunk = group.chunks[0]
              if (!firstChunk) return null
              const firstSource = group.firstSource
              const firstPrecise = toPrecisePreviewHighlight(
                firstSource.highlightData ?? undefined,
                firstSource.quote ?? null,
                `source-${group.documentId}-${firstChunk.origIndex}`
              )
              const firstTargetPage = resolveCitationTargetPage(
                firstPrecise?.highlight ? { highlight: firstPrecise.highlight } : undefined,
                firstSource.pageNumber,
              )

              return (
                /* Container, NOT a button — the name and each page pill are
                   sibling buttons inside it, and a button cannot nest. */
                <span
                  key={group.documentId}
                  className="inline-flex items-center gap-1 py-0.5 pl-2 pr-1 rounded-full border border-border bg-card text-meta"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
                          onClick={() => {
                            trackCitationClick({
                              sessionId: message.sessionId ?? undefined,
                              documentId: firstSource.documentId,
                              documentName: firstSource.documentName,
                              citationIndex: firstChunk.origIndex,
                              pageNumber: firstSource.pageNumber,
                              confidenceBand: firstSource.confidenceBand ?? null,
                              context: 'chat',
                            })
                            void usePreviewStore.getState().openPreview({
                              documentId: firstSource.documentId,
                              documentName: firstSource.documentName,
                              ...(firstTargetPage != null && { page: firstTargetPage }),
                              context: 'chat',
                              ...(firstPrecise?.highlight ? { highlight: firstPrecise.highlight } : {}),
                              ...(firstPrecise?.textHighlight ? { textHighlight: firstPrecise.textHighlight } : {}),
                            })
                          }}
                        >
                          <FileText className="h-3 w-3" />
                          <span className="truncate max-w-[200px]">
                            {group.documentName}
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-popover text-popover-foreground border shadow-md">
                        <p className="text-xs">{group.documentName}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {group.chunks.map((chunk) => {
                    const source = sources[chunk.origIndex - 1]
                    if (!source) return null

                    const precise = toPrecisePreviewHighlight(
                      source.highlightData ?? undefined,
                      source.quote ?? null,
                      `source-${source.documentId}-${chunk.origIndex}`
                    )
                    const targetPage = resolveCitationTargetPage(
                      precise?.highlight ? { highlight: precise.highlight } : undefined,
                      source.pageNumber,
                    )

                    return (
                      <TooltipProvider key={chunk.origIndex}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              className="inline-flex items-center gap-0.5 h-4 shrink-0 rounded-full bg-[hsl(var(--brand-soft))] px-1.5 text-[9.5px] font-semibold text-[hsl(var(--ai-accent-text))] hover:opacity-80 transition-opacity"
                              onClick={() => {
                                trackCitationClick({
                                  sessionId: message.sessionId ?? undefined,
                                  documentId: source.documentId,
                                  documentName: source.documentName,
                                  citationIndex: chunk.origIndex,
                                  pageNumber: source.pageNumber,
                                  confidenceBand: source.confidenceBand ?? null,
                                  context: 'chat',
                                })
                                void usePreviewStore.getState().openPreview({
                                  documentId: source.documentId,
                                  documentName: source.documentName,
                                  ...(targetPage != null && { page: targetPage }),
                                  context: 'chat',
                                  ...(precise?.highlight ? { highlight: precise.highlight } : {}),
                                  ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
                                })
                              }}
                            >
                              {chunk.displayIndex}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="bg-popover text-popover-foreground border shadow-md">
                            <p className="text-xs">{source.documentName}</p>
                            {source.pageNumber ? (
                              <p className="text-meta">
                                Page {source.pageNumber}
                              </p>
                            ) : null}
                            {(source.quote ?? source.snippet) && (
                              <p className="text-meta mt-1 max-w-[250px] line-clamp-3">
                                {source.quote ?? source.snippet}
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )
                  })}
                </span>
              )
            })}
          </div>
        )}

        {/* Library sources summary (below tender sources) */}
        {librarySources && librarySources.length > 0 && !message.isStreaming && libraryCitationMapping.usedCitations.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="text-[10px] font-medium text-muted-foreground self-center">Library:</span>
            {groupCitationsByDocument(
              libraryCitationMapping.usedCitations,
              libraryCitationMapping.origToDisplay,
              librarySources,
            ).map((group) => {
              const firstChunk = group.chunks[0]
              if (!firstChunk) return null
              const firstSource = group.firstSource
              const firstPrecise = toPrecisePreviewHighlight(
                firstSource.highlightData ?? undefined,
                firstSource.quote ?? null,
                `lib-source-${group.documentId}-${firstChunk.origIndex}`,
              )
              const firstTargetPage = resolveCitationTargetPage(
                firstPrecise?.highlight ? { highlight: firstPrecise.highlight } : undefined,
                firstSource.pageNumber,
              )

              return (
                <span
                  key={`lib-${group.documentId}`}
                  className="inline-flex items-center gap-1 py-0.5 pl-2 pr-1 rounded-full border border-border bg-card text-meta"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
                          onClick={() =>
                            void usePreviewStore.getState().openPreview({
                              documentId: firstSource.documentId,
                              documentName: firstSource.documentName,
                              ...(firstTargetPage != null && { page: firstTargetPage }),
                              context: 'chat',
                              citationLabel: `Library [L${firstChunk.displayIndex}]`,
                              ...(firstPrecise?.highlight ? { highlight: firstPrecise.highlight } : {}),
                              ...(firstPrecise?.textHighlight ? { textHighlight: firstPrecise.textHighlight } : {}),
                            })
                          }
                        >
                          <BookOpen className="h-3 w-3" />
                          <span className="truncate max-w-[200px]">
                            {group.documentName}
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-popover text-popover-foreground border shadow-md">
                        <p className="text-xs font-medium">{group.documentName}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {group.chunks.map((chunk) => {
                    const source = librarySources[chunk.origIndex - 1]
                    if (!source) return null

                    const precise = toPrecisePreviewHighlight(
                      source.highlightData ?? undefined,
                      source.quote ?? null,
                      `lib-source-${source.documentId}-${chunk.origIndex}`,
                    )
                    const targetPage = resolveCitationTargetPage(
                      precise?.highlight ? { highlight: precise.highlight } : undefined,
                      source.pageNumber,
                    )

                    return (
                      <TooltipProvider key={`lib-${chunk.origIndex}`}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              className="inline-flex items-center gap-0.5 h-4 shrink-0 rounded-full bg-[hsl(var(--brand-soft))] px-1.5 text-[9.5px] font-semibold text-[hsl(var(--ai-accent-text))] hover:opacity-80 transition-opacity"
                              onClick={() =>
                                void usePreviewStore.getState().openPreview({
                                  documentId: source.documentId,
                                  documentName: source.documentName,
                                  ...(targetPage != null && { page: targetPage }),
                                  context: 'chat',
                                  citationLabel: `Library [L${chunk.displayIndex}]`,
                                  ...(precise?.highlight ? { highlight: precise.highlight } : {}),
                                  ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
                                })
                              }
                            >
                              L{chunk.displayIndex}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="bg-popover text-popover-foreground border shadow-md">
                            <p className="text-xs font-medium">{source.documentName}</p>
                            {source.pageNumber ? (
                              <p className="text-meta">
                                Page {source.pageNumber}
                              </p>
                            ) : null}
                            {(source.quote ?? source.snippet) && (
                              <p className="text-meta mt-1 max-w-[250px] line-clamp-3">
                                {source.quote ?? source.snippet}
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )
                  })}
                </span>
              )
            })}
          </div>
        )}

        {/* Honest retrieval-status line (§2.2). Quiet, no card. Renders only when
            a store has attached a pre-derived line (orchestrator transport); the
            legacy assistant surface never sets it, so it is unaffected today. */}
        {isAssistant && !message.isStreaming && message.retrievalDetail && (
          <p className="mt-1 text-meta">{message.retrievalDetail}</p>
        )}

        {/* "Saved to memory · Undo" — one line per item the AI saved this reply. */}
        {isAssistant && onUndoMemory && (
          <MemorySavedLines
            lines={message.memorySaved}
            onUndo={(memoryId) => onUndoMemory(message.id, memoryId)}
          />
        )}
      </div>
    </div>
  )
}
